/**
 * File Service APIs 
 * Handles file uploads, downloads, and management
 */

import { apiRequest } from "./apiClient";
import { EmptyResponse } from "./types";

// ========================================
// Types & Interfaces
// ========================================

export interface UploadDocumentResponse {
    data: {
        succeeded: boolean;
        id: string; // This is the fileId
        filePath: string;
    };
    code?: string;
    message?: string;
    exceptionName?: string;
    stackTrace?: string;
}

export interface PresignedUrlResponse {
    data: {
        downloadUrl: string;
    };
    code?: string;
    message?: string;
    exceptionName?: string;
    stackTrace?: string;
}

// ========================================
// Upload APIs
// ========================================

/**
 * Upload a document file for a process (Flowchart)
 * Files must be: pdf, xls, doc
 * This uploads to temporary storage and returns a fileId
 */
export const uploadProcessDocument = async (
    file: File
): Promise<UploadDocumentResponse> => {
    try {
        const formData = new FormData();
        formData.append('File', file); // Note: Backend requires 'File' with capital F

        return await apiRequest<UploadDocumentResponse, FormData>(
            'POST',
            `/file-uploads/process/document`,
            formData,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to upload document',
        };
    }
};

/**
 * Delete a document file
 */
export const deleteProcessDocument = async (
    processId: string,
    fileId: string,
): Promise<EmptyResponse> => {
    try {
        return await apiRequest<EmptyResponse, never>(
            'DELETE',
            `/file-uploads/process/document/${processId}/${fileId}`,
            undefined,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to delete document',
        };
    }
};

/**
 * Upload a test sheet file for a process
 * Files must be: pdf, xls, doc
 * Only ONE test sheet per process
 * This uploads to temporary storage and returns a fileId
 */
export const uploadProcessTestSheet = async (
    file: File
): Promise<UploadDocumentResponse> => {
    try {
        const formData = new FormData();
        formData.append('File', file); // Note: Backend requires 'File' with capital F

        return await apiRequest<UploadDocumentResponse, FormData>(
            'POST',
            `/file-uploads/process/test-sheet`,
            formData,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to upload test sheet',
        };
    }
};

/**
 * Delete a test sheet file
 */
export const deleteProcessTestSheet = async (
    processId: string,
    fileId: string,
): Promise<EmptyResponse> => {
    try {
        return await apiRequest<EmptyResponse, never>(
            'DELETE',
            `/file-uploads/process/test-sheet/${processId}/${fileId}`,
            undefined,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to delete test sheet',
        };
    }
};

/**
 * Upload a test sheet file for a control
 * Files must be: pdf, xls, doc
 * Only ONE test sheet per control
 * This uploads to temporary storage and returns a fileId
 */
export const uploadControlTestSheet = async (
    file: File
): Promise<UploadDocumentResponse> => {
    try {
        const formData = new FormData();
        formData.append('File', file); // Note: Backend requires 'File' with capital F

        return await apiRequest<UploadDocumentResponse, FormData>(
            'POST',
            `/file-uploads/control/test-sheet`,
            formData,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to upload test sheet',
        };
    }
};

/**
 * Delete a test sheet file
 */
export const deleteControlTestSheet = async (
    controlId: string,
    fileId: string,
): Promise<EmptyResponse> => {
    try {
        return await apiRequest<EmptyResponse, never>(
            'DELETE',
            `/file-uploads/control/test-sheet/${controlId}/${fileId}`,
            undefined,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to delete test sheet',
        };
    }
};

// ========================================
// Download APIs
// ========================================

/**
 * Get a presigned download URL for a file
 * @param fileId - The ID of the file to download
 * @returns Presigned URL response with download URL
 */
export const getPresignedDownloadUrl = async (
    fileId: string
): Promise<PresignedUrlResponse> => {
    try {
        return await apiRequest<PresignedUrlResponse, never>(
            'GET',
            `/file-uploads/presigned-url/${fileId}`,
            undefined,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to get download URL',
        };
    }
};

/**
 * Download a file using its presigned URL and save with custom filename
 * Uses direct link approach to avoid CORS issues with S3 presigned URLs
 * @param fileId - The ID of the file to download
 * @param fileName - The desired filename for the downloaded file (optional, S3 handles this via response-content-disposition)
 */
export const downloadFile = async (
    fileId: string,
    _fileName?: string // fileName is optional since S3 presigned URL includes disposition
): Promise<void> => {
    try {
        // Step 1: Get presigned URL from backend
        const response = await getPresignedDownloadUrl(fileId);
        
        if (!response.data?.downloadUrl) {
            throw new Error('No download URL returned');
        }
        
        // Step 2: Open the presigned URL directly
        // The presigned URL includes response-content-disposition=attachment
        // which triggers browser download without CORS issues
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.target = '_blank'; // Open in new tab to handle download
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to download file',
        };
    }
};

// ========================================
// Company logo APIs
// ========================================

/**
 * Upload company logo image
 * POST /api/v1/file-uploads/company-logo/{companyId}
 * Valid formats: png, jpeg, webp. Replaces existing logo if any.
 */
export const uploadCompanyLogo = async (
    companyId: string,
    file: File
): Promise<UploadDocumentResponse> => {
    try {
        const formData = new FormData();
        formData.append('File', file);

        return await apiRequest<UploadDocumentResponse, FormData>(
            'POST',
            `/file-uploads/company-logo/${companyId}`,
            formData,
            true
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to upload company logo',
        };
    }
};

/**
 * Remove company logo (server-side delete).
 */
export const deleteCompanyLogo = async (
    companyId: string
): Promise<EmptyResponse> => {
    try {
        return await apiRequest<EmptyResponse, never>(
            'DELETE',
            `/file-uploads/company-logo/${companyId}`,
            undefined,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to delete company logo',
        };
    }
};

// ========================================
// COSO Compliance Document APIs
// ========================================

/**
 * Upload a document file for COSO Compliance
 * POST /api/v1/file-uploads/coso-compliance/{annualReportId}
 * Automatically saves file upload to annual report
 */
export const uploadCOSOComplianceDocument = async (
    annualReportId: string,
    file: File
): Promise<UploadDocumentResponse> => {
    try {
        const formData = new FormData();
        formData.append('File', file); // Note: Backend requires 'File' with capital F

        return await apiRequest<UploadDocumentResponse, FormData>(
            'POST',
            `/file-uploads/coso-compliance/${annualReportId}`,
            formData,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to upload COSO compliance document',
        };
    }
};

/**
 * Delete a COSO Compliance document file
 * DELETE /api/v1/file-uploads/coso-compliance/{annualReportId}/{fileId}
 */
export const deleteCOSOComplianceDocument = async (
    annualReportId: string,
    fileId: string,
): Promise<EmptyResponse> => {
    try {
        return await apiRequest<EmptyResponse, never>(
            'DELETE',
            `/file-uploads/coso-compliance/${annualReportId}/${fileId}`,
            undefined,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to delete COSO compliance document',
        };
    }
};

// ========================================
// Audit Planning Document APIs (EX9 Tab 2)
// ========================================

/**
 * Upload a document file for Audit Planning
 * POST /api/v1/file-uploads/audit/{annualReportId}
 * Automatically saves file upload to annual report
 * Files uploaded here are validated to be valid files
 */
export const uploadAuditPlanningDocument = async (
    annualReportId: string,
    file: File
): Promise<UploadDocumentResponse> => {
    try {
        const formData = new FormData();
        formData.append('File', file); // Note: Backend requires 'File' with capital F

        return await apiRequest<UploadDocumentResponse, FormData>(
            'POST',
            `/file-uploads/audit/${annualReportId}`,
            formData,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to upload audit planning document',
        };
    }
};

/**
 * Delete an Audit Planning document file
 * DELETE /api/v1/file-uploads/audit/{annualReportId}/{fileId}
 * Removes a document file from audit planning docs
 */
export const deleteAuditPlanningDocument = async (
    annualReportId: string,
    fileId: string,
): Promise<EmptyResponse> => {
    try {
        return await apiRequest<EmptyResponse, never>(
            'DELETE',
            `/file-uploads/audit/${annualReportId}/${fileId}`,
            undefined,
            true // Authentication required
        );
    } catch (error) {
        if (error && typeof error === 'object' && 'code' in error) {
            throw error;
        }
        throw {
            code: 'UNEXPECTED_ERROR',
            message: error instanceof Error ? error.message : 'Failed to delete audit planning document',
        };
    }
};
